import { IMarketFeedResponse } from "@finbackoffice/clientbff-client";
import {
    getMarketGroupId,
    isHandicapMarket,
    isHandicapMarketBC,
    isScoreMarket,
    isScoreMarketBC,
    MarketGroupData,
    MarketGroupIds,
    MarketParamBC,
    sortByFavoriteId,
    sortByNumberAfterLastSpace,
    sortByNumberBetweenBrackets,
    sortSetWinnerMarkets,
    TemplateIdsMap,
} from "@finbackoffice/fe-core";

// Template IDs that use handicap sorting
const HANDICAP_TEMPLATE_IDS = [
    TemplateIdsMap.HANDICAP,
    TemplateIdsMap.HANDICAP_INCL_EXTRA_INNINGS,
    TemplateIdsMap.INNINGS_1TO5_HANDICAP,
    TemplateIdsMap.INNINGNUMBER_INNING_HANDICAP,
    TemplateIdsMap.INNINGS_FROMVALUE_TOVALUE_HANDICAP,
    TemplateIdsMap.QUARTERNUMBER_QUARTER_HANDICAP,
    TemplateIdsMap.PERIODNUMBER_PERIOD_HANDICAP,
];

// Template IDs that use total sorting
const TOTAL_TEMPLATE_IDS = [
    TemplateIdsMap.TOTAL,
    TemplateIdsMap.COMPETITOR1_TOTAL,
    TemplateIdsMap.COMPETITOR2_TOTAL,
    TemplateIdsMap.TOTAL_1ST_HALF,
    TemplateIdsMap.TOTAL_1ST_HALF_COMPETITOR1,
    TemplateIdsMap.TOTAL_1ST_HALF_COMPETITOR2,
    TemplateIdsMap.TOTAL_2ND_HALF,
    TemplateIdsMap.TOTAL_2ND_HALF_COMPETITOR1,
    TemplateIdsMap.TOTAL_2ND_HALF_COMPETITOR2,
    TemplateIdsMap.TOTAL_POINTS,
    TemplateIdsMap.TOTAL_INCL_EXTRA_INNINGS,
    TemplateIdsMap.INNINGS_1TO5_TOTAL,
    TemplateIdsMap.INNINGS_1TO5_COMPETITOR1_TOTAL,
    TemplateIdsMap.INNINGS_1TO5_COMPETITOR2_TOTAL,
    TemplateIdsMap.INNINGNUMBER_INNING_TOTAL,
    TemplateIdsMap.INNINGS_FROMVALUE_TOVALUE_TOTAL,
    TemplateIdsMap.QUARTERNUMBER_QUARTER_TOTAL,
    TemplateIdsMap.QUARTERNUMBER_QUARTER_COMPETITOR1_TOTAL,
    TemplateIdsMap.QUARTERNUMBER_QUARTER_COMPETITOR2_TOTAL,
    TemplateIdsMap.PERIODNUMBER_PERIOD_TOTAL,
    TemplateIdsMap.INNINGS_FROMVALUE_TOVALUE_COMPETITOR1_TOTAL,
    TemplateIdsMap.INNINGS_FROMVALUE_TOVALUE_COMPETITOR2_TOTAL,
];

export const sortMarketsInGroups = (marketGroups: Record<string, MarketGroupData>) => {
    const groupIds = Object.keys(marketGroups);

    // Categorize IDs in a single pass
    const categorizedIds = {
        handicap: [] as string[],
        total: [] as string[],
    };

    groupIds.forEach((id) => {
        // Check if ID contains any handicap template ID
        if (HANDICAP_TEMPLATE_IDS.some((templateId) => id.includes(templateId.toString()))) {
            categorizedIds.handicap.push(id);
        }
        // Check if ID contains any total template ID
        else if (TOTAL_TEMPLATE_IDS.some((templateId) => id.includes(templateId.toString()))) {
            categorizedIds.total.push(id);
        }
    });

    // Sort handicap markets
    categorizedIds.handicap.forEach((item) => {
        const marketGroup = marketGroups[item];
        if (marketGroup && marketGroup.markets.length > 1) {
            marketGroup.markets.sort((a, b) => sortByNumberBetweenBrackets(a, b));
        }
    });

    // Sort total markets
    categorizedIds.total.forEach((item) => {
        const marketGroup = marketGroups[item];
        if (marketGroup && marketGroup.markets.length > 1) {
            marketGroup.markets.sort((a, b) => sortByNumberAfterLastSpace(a, b));
        }
    });

    return marketGroups;
};

export const sortMarketsInGroupsBCFeed = (marketGroups: Record<string, MarketGroupData>) => {
    Object.values(marketGroups).forEach((marketGroup) => {
        if (marketGroup.markets.length <= 1) return;

        const hasHcpParameter = marketGroup.markets.every((market) =>
            market.parameters.some((param) => param.label === MarketParamBC.Hcp),
        );

        if (hasHcpParameter) {
            marketGroup.markets.sort((a, b) => {
                const hcpA = a.parameters.find((param) => param.label === MarketParamBC.Hcp)?.value;
                const hcpB = b.parameters.find((param) => param.label === MarketParamBC.Hcp)?.value;

                if (!hcpA || !hcpB) return 0;

                const numA = parseFloat(hcpA);
                const numB = parseFloat(hcpB);

                return numA - numB;
            });
        }
    });

    return marketGroups;
};

export const sortMarketGroups =
    (favoriteMarketsIds: string[]) =>
    (a: MarketGroupData, b: MarketGroupData): number => {
        // 1. Sort by favoriteMarketsIds
        const favoriteSort = sortByFavoriteId(favoriteMarketsIds)(a, b);
        if (favoriteSort !== 0) return favoriteSort;

        // 2. Special sort for SETNUMBER_SET_WINNER
        const aFirstMarket = a.markets?.[0];
        const bFirstMarket = b.markets?.[0];
        if (
            aFirstMarket?.template_id === TemplateIdsMap.SETNUMBER_SET_WINNER &&
            bFirstMarket?.template_id === TemplateIdsMap.SETNUMBER_SET_WINNER
        ) {
            const setWinnerSort = sortSetWinnerMarkets(aFirstMarket, bFirstMarket);
            if (setWinnerSort !== 0) return setWinnerSort;
        }

        // 3. Fallback to rank
        return a.rank - b.rank;
    };

export const sortMarketGroupsBC =
    (favoriteMarketsIds: string[]) =>
    (a: MarketGroupData, b: MarketGroupData): number => {
        // 1. Sort by favoriteMarketsIds
        const favoriteSort = sortByFavoriteId(favoriteMarketsIds)(a, b);
        if (favoriteSort !== 0) return favoriteSort;

        // 3. Fallback to rank
        return a.rank - b.rank;
    };

const getMarketGroupType = (templateId: number) => {
    if (isScoreMarket(templateId)) {
        return "score";
    } else if (isHandicapMarket(templateId)) {
        return "handicap";
    }
    return "default";
};

export const convertMarketsIntoMarketGroups = (
    markets: IMarketFeedResponse[],
    homeTeamName: string,
    awayTeamName: string,
): Record<string, MarketGroupData> => {
    const marketGroups: Record<string, MarketGroupData> = {};

    const createMarketGroup = (market: IMarketFeedResponse, groupId: string) => {
        marketGroups[groupId] = new MarketGroupData(
            market,
            groupId,
            homeTeamName,
            awayTeamName,
            market.template_id === TemplateIdsMap.WINNER_INCL_OVERTIME ? 0 : market.template_id,
            getMarketGroupType(market.template_id),
        );
    };

    markets.forEach((market: IMarketFeedResponse) => {
        const canGroupByTemplate = MarketGroupIds.includes(market.template_id);
        const groupId = canGroupByTemplate ? getMarketGroupId(market) : market.id.toString();

        if (canGroupByTemplate) {
            const existingGroup = marketGroups[groupId];
            if (existingGroup) {
                existingGroup.markets.push(market);
            } else {
                createMarketGroup(market, groupId);
            }
        } else {
            createMarketGroup(market, groupId);
        }
    });

    return marketGroups;
};

const getMarketGroupTypeBC = (templateId: number) => {
    if (isScoreMarketBC(templateId)) {
        return "score";
    } else if (isHandicapMarketBC(templateId)) {
        return "handicap";
    }

    return "default";
};

export const convertMarketsIntoMarketGroupsBCFeed = (
    markets: IMarketFeedResponse[],
    homeTeamName: string,
    awayTeamName: string,
): Record<string, MarketGroupData> => {
    const marketGroups: Record<string, MarketGroupData> = {};

    const createMarketGroup = (market: IMarketFeedResponse, groupId: string) => {
        marketGroups[groupId] = new MarketGroupData(
            market,
            groupId,
            homeTeamName,
            awayTeamName,
            market.order,
            getMarketGroupTypeBC(market.template_id),
        );
    };

    const getParametersKey = (market: IMarketFeedResponse): string => {
        const relevantParams = [
            MarketParamBC.P,
            MarketParamBC.Pw,
            MarketParamBC.S,
            MarketParamBC.Sw,
        ];
        return market.parameters
            .filter((param) => relevantParams.includes(param.label as MarketParamBC))
            .map((param) => `${param.label}:${param.value}`)
            .sort()
            .join("|");
    };

    markets.forEach((market: IMarketFeedResponse) => {
        const canGroupByTemplate = market.outcomes_count <= 3;

        const parametersKey = getParametersKey(market);
        const groupId = canGroupByTemplate
            ? `${market.template_id}${parametersKey ? "_" : ""}${parametersKey}`
            : market.id.toString();

        const existingGroup = marketGroups[groupId];

        if (existingGroup) {
            existingGroup.markets.push(market);
        } else {
            createMarketGroup(market, groupId);
        }
    });

    return marketGroups;
};
